import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Typography,
    IconButton,
    Grid,
    useTheme,
    Stack,
    Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { tokens } from "../../theme";

const ProjectModal = ({ open, onClose, project }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // Galleries differ in length, so start each project back at its first image
    useEffect(() => {
        setCurrentImageIndex(0);
    }, [project]);

    if (!project) return null;

    const images = project.images && project.images.length
        ? project.images
        : (project.imgSrc ? [{ url: project.imgSrc, alt: project.imgAlt }] : []);
    const paragraphs = Array.isArray(project.fullDescription)
        ? project.fullDescription
        : [project.fullDescription];

    const handlePrevImage = () => {
        setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    };

    const handleNextImage = () => {
        setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="project-modal-title"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: { xs: 2, sm: 4 }
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: '900px',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    backgroundColor: colors.background[200],
                    borderRadius: 2,
                    p: { xs: 2, sm: 3, md: 4 }
                }}
            >
                <IconButton
                    onClick={onClose}
                    aria-label="Close project details"
                    sx={{
                        position: 'absolute',
                        right: { xs: 8, sm: 16 },
                        top: { xs: 8, sm: 16 },
                        zIndex: 2,
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        color: 'white',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                    {images.length > 0 && (
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    height: { xs: '250px', sm: '300px', md: '400px' },
                                    borderRadius: 1,
                                    overflow: 'hidden'
                                }}
                            >
                                {/* Navigation Buttons */}
                                {images.length > 1 && (
                                    <>
                                        <IconButton
                                            onClick={handlePrevImage}
                                            aria-label="Previous image"
                                            sx={{
                                                position: 'absolute',
                                                left: { xs: 8, sm: 16 },
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                                                color: 'white',
                                                zIndex: 1,
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                                }
                                            }}
                                        >
                                            <NavigateBeforeIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={handleNextImage}
                                            aria-label="Next image"
                                            sx={{
                                                position: 'absolute',
                                                right: { xs: 8, sm: 16 },
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                                                color: 'white',
                                                zIndex: 1,
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                                }
                                            }}
                                        >
                                            <NavigateNextIcon />
                                        </IconButton>
                                    </>
                                )}

                                {/* Current Image */}
                                <img
                                    src={images[currentImageIndex].url}
                                    alt={images[currentImageIndex].alt}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                    }}
                                />

                                {/* Image Counter */}
                                {images.length > 1 && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            bottom: { xs: 8, sm: 16 },
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            backgroundColor: 'rgba(0, 0, 0, 0.4)',
                                            color: 'white',
                                            padding: '4px 12px',
                                            borderRadius: 1,
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                            fontWeight: 500,
                                            zIndex: 1
                                        }}
                                    >
                                        {currentImageIndex + 1} / {images.length}
                                    </Box>
                                )}
                            </Box>

                            {/* Image Caption */}
                            <Typography
                                variant="body2"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    textAlign: 'center',
                                    mt: 1,
                                    fontSize: { xs: '0.875rem', sm: '1rem' }
                                }}
                            >
                                {images[currentImageIndex].alt}
                            </Typography>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Stack spacing={3}>
                            <Box>
                                <Typography
                                    id="project-modal-title"
                                    variant="h4"
                                    sx={{
                                        color: theme.palette.text.primary,
                                        fontWeight: 'bold',
                                        fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                        pr: { xs: 5, sm: 6 },
                                        mb: 0.5
                                    }}
                                >
                                    {project.title}
                                </Typography>
                                {project.subtitle && (
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: theme.palette.text.secondary,
                                            fontSize: { xs: '1rem', md: '1.1rem' },
                                            mb: 2
                                        }}
                                    >
                                        {project.subtitle}
                                    </Typography>
                                )}
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                                        gap: 2
                                    }}
                                >
                                    {project.details.map((detail) => (
                                        <Box key={detail.label}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: theme.palette.text.secondary,
                                                    fontSize: { xs: '0.875rem', md: '0.9rem' }
                                                }}
                                            >
                                                {detail.label}
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    color: theme.palette.text.primary,
                                                    fontWeight: 500,
                                                    fontSize: { xs: '1rem', md: '1.05rem' }
                                                }}
                                            >
                                                {detail.value}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>

                            <Divider sx={{ borderColor: theme.palette.divider }} />

                            <Box>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: theme.palette.text.primary,
                                        fontWeight: 600,
                                        mb: 2,
                                        fontSize: { xs: '1.1rem', md: '1.25rem' }
                                    }}
                                >
                                    Project Overview
                                </Typography>
                                <Stack spacing={2}>
                                    {paragraphs.map((paragraph, index) => (
                                        <Typography
                                            key={index}
                                            variant="body1"
                                            sx={{
                                                color: theme.palette.text.secondary,
                                                lineHeight: 1.7,
                                                fontSize: { xs: '0.95rem', md: '1rem' }
                                            }}
                                        >
                                            {paragraph}
                                        </Typography>
                                    ))}
                                </Stack>
                            </Box>

                            <Box>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: theme.palette.text.primary,
                                        fontWeight: 600,
                                        mb: 2,
                                        fontSize: { xs: '1.1rem', md: '1.25rem' }
                                    }}
                                >
                                    Scope of Works
                                </Typography>
                                <Stack spacing={1}>
                                    {project.highlights.map((highlight, index) => (
                                        <Typography
                                            key={index}
                                            variant="body1"
                                            sx={{
                                                color: theme.palette.text.secondary,
                                                fontSize: { xs: '0.95rem', md: '1rem' },
                                                display: 'flex',
                                                alignItems: 'center',
                                                '&:before': {
                                                    content: '"•"',
                                                    color: theme.palette.primary.main,
                                                    fontWeight: 'bold',
                                                    marginRight: '8px',
                                                    fontSize: '1.2em'
                                                }
                                            }}
                                        >
                                            {highlight}
                                        </Typography>
                                    ))}
                                </Stack>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
        </Modal>
    );
};

export default ProjectModal;
